"use client";

import DOMPurify from "isomorphic-dompurify";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { AnnouncementBar } from "@/components/announcement-bar";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { WhatsAppButton } from "@/components/whatsapp-button";
import { MobileTabBar } from "@/components/mobile-tab-bar";
import type { InfoPageNode } from "@/lib/api";

function unwrapTopLevelDivs(html: string): string {
  // Lightspeed often wraps body content in a non-semantic <div>, which
  // breaks our direct-child Tailwind selectors (`[&>p]`, `[&>ul]`, etc.)
  // and leaves lists / paragraphs inside the div completely unstyled.
  // Iteratively peel off any <div> that has no child <div>, plus empty
  // ones. Innermost-first is the safe order — after a few passes every
  // div is gone.
  let prev = "";
  while (prev !== html) {
    prev = html;
    html = html.replace(/<div\b[^>]*>\s*<\/div>/gi, "");
    html = html.replace(
      /<div\b[^>]*>((?:(?!<div\b)[\s\S])*?)<\/div>/gi,
      "$1",
    );
  }
  return html;
}

function stripTableInlineStyling(html: string): string {
  // Lightspeed tables ship with `border="1" cellpadding="25"` and inline
  // `style="background-color: #f6f6f6; height: 144px"` etc. — strip the
  // HTML4 attrs and any inline `style` on table-related tags so our CSS
  // can own the look.
  const tags = ["table", "thead", "tbody", "tfoot", "tr", "td", "th"];
  let out = html;
  for (const tag of tags) {
    out = out.replace(
      new RegExp(`<${tag}\\b([^>]*)>`, "gi"),
      (_m, attrs: string) => {
        const cleaned = attrs
          .replace(/\sstyle\s*=\s*"[^"]*"/gi, "")
          .replace(/\sstyle\s*=\s*'[^']*'/gi, "")
          .replace(/\s(border|cellpadding|cellspacing|bgcolor|width|height|align|valign)\s*=\s*"[^"]*"/gi, "")
          .replace(/\s(border|cellpadding|cellspacing|bgcolor|width|height|align|valign)\s*=\s*'[^']*'/gi, "")
          .replace(/\s(border|cellpadding|cellspacing|bgcolor|width|height|align|valign)\s*=\s*\S+/gi, "");
        return `<${tag}${cleaned}>`;
      },
    );
  }
  return out;
}

function promoteTableHeaderRow(html: string): string {
  // Lightspeed authors table headers as the first <tr> of <td><strong>X</strong></td>
  // cells. Promote those td>strong into real <th>s so default header
  // styling (uppercase eyebrow, surface bg) applies.
  return html.replace(
    /(<table\b[^>]*>[\s\S]*?<tbody\b[^>]*>)\s*(<tr\b[^>]*>[\s\S]*?<\/tr>)/i,
    (match, tableOpen: string, firstRow: string) => {
      const cells = firstRow.match(/<td\b[^>]*>([\s\S]*?)<\/td>/gi) || [];
      if (cells.length < 2) return match;
      const allStrongOnly = cells.every((cell) => {
        const inner = cell.replace(/^<td\b[^>]*>/i, "").replace(/<\/td>\s*$/i, "");
        const stripped = inner.replace(/<strong>([\s\S]*?)<\/strong>/gi, "$1").trim();
        const original = inner.replace(/&nbsp;/g, " ").trim();
        return /<strong\b/i.test(inner) && stripped === original.replace(/<\/?strong>/gi, "");
      });
      if (!allStrongOnly) return match;
      const promoted = firstRow.replace(
        /<td(\b[^>]*)>([\s\S]*?)<\/td>/gi,
        (_m, attrs: string, inner: string) => {
          const text = inner.replace(/<\/?strong>/gi, "").trim();
          return `<th${attrs}>${text}</th>`;
        },
      );
      return `${tableOpen}${promoted}`;
    },
  );
}

function promoteBrLineLists(html: string): string {
  // Lightspeed authors lists by hammering <br /> inside a single <p>.
  // Catch paragraphs that are really just N short lines and promote them
  // into a real <ul.ingredient-list> so we can render a multi-column,
  // properly-spaced grid instead of a dense wall of text.
  return html.replace(
    /<p\b[^>]*>([\s\S]*?)<\/p>/gi,
    (match, inner: string) => {
      const segments = inner
        .split(/<br\s*\/?>/gi)
        .map((s) => s.replace(/&nbsp;/g, " ").trim())
        .filter((s) => s.length > 0);
      if (segments.length < 5) return match;
      const looksLikeList = segments.every((seg) => {
        const text = seg.replace(/<[^>]+>/g, "").trim();
        return (
          text.length > 0 &&
          text.length <= 80 &&
          !/[.!?:]$/.test(text) &&
          (text.match(/\s/g) || []).length <= 8
        );
      });
      if (!looksLikeList) return match;
      const items = segments
        .map((seg) => `<li>${seg.replace(/<[^>]+>/g, "").trim()}</li>`)
        .join("");
      return `<ul class="ingredient-list">${items}</ul>`;
    },
  );
}

function stripLegacyButtons(html: string): string {
  // Drop the salmon CTA images Lightspeed left in the body
  // (button-bekijk-onze-producten.png, button-naar-de-homepage.png, etc.)
  let out = html.replace(
    /<a[^>]*>\s*<img[^>]*\/button-[^>]*>\s*<\/a>\s*/gi,
    "",
  );
  // Drop the leading <h2> if it duplicates the page title we're already
  // rendering as <h1> outside the article. Lightspeed bodies often start
  // with one. Trim whitespace after to avoid an empty first child.
  out = out.replace(/^\s*<h2[^>]*>[\s\S]*?<\/h2>\s*/i, "");
  // Headings that now only wrap whitespace / <br /> after the strip
  out = out.replace(
    /<(h[1-6])[^>]*>\s*(?:<br\s*\/?>\s*|&nbsp;|\s)*<\/\1>\s*/gi,
    "",
  );
  // Promote bullet-list <h4> blocks (Lightspeed authored "kenmerken" via
  // <h4>• ITEM<br />• ITEM</h4>) into a real <ul>, so we can give them
  // proper list styling instead of leaving them as a giant heading.
  out = out.replace(
    /<h4[^>]*>([\s\S]*?)<\/h4>/gi,
    (match, inner: string) => {
      const lines = inner
        .split(/<br\s*\/?>/gi)
        .map((s) => s.replace(/^\s*[•·\-–—]\s*/u, "").trim())
        .filter((s) => s.length > 0 && /^[•·\-–—\s]*\S/u.test(s));
      const allBullets =
        lines.length >= 2 &&
        inner.replace(/<[^>]+>/g, "").trim().match(/^[•·\-–—]/u);
      if (!allBullets) return match;
      const items = lines
        .map((s) => `<li>${s.replace(/<[^>]+>/g, "").trim()}</li>`)
        .join("");
      return `<ul class="kenmerken-list">${items}</ul>`;
    },
  );
  return out;
}

type TocEntry = { id: string; level: 2 | 3; text: string };

function buildToc(html: string): { html: string; toc: TocEntry[] } {
  const toc: TocEntry[] = [];
  const used = new Set<string>();
  const slugify = (raw: string) => {
    const base =
      raw
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .slice(0, 60) || "section";
    let candidate = base;
    let n = 2;
    while (used.has(candidate)) candidate = `${base}-${n++}`;
    used.add(candidate);
    return candidate;
  };

  const out = html.replace(
    /<(h[23])([^>]*)>([\s\S]*?)<\/\1>/gi,
    (match, tag: string, attrs: string, inner: string) => {
      const text = inner
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (!text) return match;
      if (/\bid=/i.test(attrs)) return match;
      const id = slugify(text);
      toc.push({ id, level: tag.toLowerCase() === "h2" ? 2 : 3, text });
      return `<${tag}${attrs} id="${id}">${inner}</${tag}>`;
    },
  );

  return { html: out, toc };
}

export function InfoPageRenderer({
  page,
  breadcrumbs,
}: {
  page: InfoPageNode;
  breadcrumbs: Array<{ slug: string; title: string }>;
}) {
  const t = useTranslations("infoHub");
  const sanitized = DOMPurify.sanitize(page.content || "", {
    ALLOWED_TAGS: [
      "p", "br", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
      "strong", "em", "b", "i", "u", "a", "span", "blockquote",
      "table", "thead", "tbody", "tr", "td", "th",
      "img", "figure", "figcaption", "iframe",
      "div",
    ],
    ALLOWED_ATTR: [
      "href", "target", "rel",
      "src", "alt", "width", "height", "title",
      "class", "style",
      "allow", "allowfullscreen", "frameborder",
      "id",
    ],
    ALLOWED_URI_REGEXP:
      /^(?:(?:(?:https?|mailto|tel|ftp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))|\/|#)/i,
  });

  const cleaned = promoteTableHeaderRow(
    promoteBrLineLists(
      stripTableInlineStyling(
        unwrapTopLevelDivs(stripLegacyButtons(sanitized)),
      ),
    ),
  );
  const { html: safeHtml, toc } = buildToc(cleaned);

  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <div className="content-wrapper py-10 lg:py-16">
          <nav className="flex items-center gap-1.5 text-xs text-text-tertiary mb-6 flex-wrap">
            <Link href="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            {breadcrumbs.map((b, i) => (
              <span key={b.slug} className="flex items-center gap-1.5">
                <span>/</span>
                {i === breadcrumbs.length - 1 ? (
                  <span className="text-text-secondary">{b.title}</span>
                ) : (
                  <Link
                    href={`/${b.slug}`}
                    className="hover:text-foreground transition-colors"
                  >
                    {b.title}
                  </Link>
                )}
              </span>
            ))}
          </nav>

          <header className="max-w-3xl mb-10 lg:mb-14">
            <p className="label-sm mb-3">
              {breadcrumbs.length > 1
                ? breadcrumbs[breadcrumbs.length - 2].title
                : "Informatie"}
            </p>
            <h1 className="heading-lg">{page.title}</h1>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] gap-10 lg:gap-20">
            <div className="min-w-0">
              {page.hasContent ? (
                <article
                  className="info-page-content text-[15.5px] text-text-body leading-[1.8]
                    [&>p]:mb-5 [&>p]:max-w-[68ch] [&>p:last-child]:mb-0
                    [&>p:first-child]:text-[17px] [&>p:first-child]:leading-[1.7] [&>p:first-child]:text-text-secondary [&>p:first-child]:mb-7
                    [&>h2]:max-w-[68ch] [&>h3]:max-w-[68ch] [&>h4]:max-w-[68ch]
                    [&>ul:not(.kenmerken-list):not(.ingredient-list)]:max-w-[68ch] [&>ol]:max-w-[68ch]
                    [&>blockquote]:max-w-[68ch]
                    [&>h2]:font-[family-name:var(--font-cormorant)] [&>h2]:text-3xl [&>h2]:font-normal [&>h2]:text-foreground [&>h2]:mt-12 [&>h2]:mb-4 [&>h2]:scroll-mt-28 [&>h2]:tracking-tight
                    [&>h3]:font-[family-name:var(--font-cormorant)] [&>h3]:text-2xl [&>h3]:font-normal [&>h3]:text-foreground [&>h3]:mt-10 [&>h3]:mb-3 [&>h3]:scroll-mt-28 [&>h3]:tracking-tight
                    [&>h3]:relative [&>h3]:pl-4 [&>h3]:before:content-[''] [&>h3]:before:absolute [&>h3]:before:left-0 [&>h3]:before:top-2.5 [&>h3]:before:bottom-2.5 [&>h3]:before:w-[2px] [&>h3]:before:bg-foreground/40
                    [&>h4]:font-semibold [&>h4]:uppercase [&>h4]:tracking-[0.1em] [&>h4]:text-[12px] [&>h4]:text-text-tertiary [&>h4]:mt-8 [&>h4]:mb-3
                    [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-5 [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:mb-5
                    [&>ul>li]:mb-1.5 [&>ol>li]:mb-1.5 [&>ul>li]:marker:text-foreground/40 [&>ol>li]:marker:text-foreground/40
                    [&>ul.kenmerken-list]:list-none [&>ul.kenmerken-list]:p-6 [&>ul.kenmerken-list]:bg-surface [&>ul.kenmerken-list]:rounded-2xl [&>ul.kenmerken-list]:grid [&>ul.kenmerken-list]:grid-cols-1 [&>ul.kenmerken-list]:sm:grid-cols-2 [&>ul.kenmerken-list]:gap-x-6 [&>ul.kenmerken-list]:gap-y-2.5 [&>ul.kenmerken-list]:my-6
                    [&>ul.kenmerken-list>li]:flex [&>ul.kenmerken-list>li]:items-center [&>ul.kenmerken-list>li]:gap-2.5 [&>ul.kenmerken-list>li]:m-0 [&>ul.kenmerken-list>li]:text-sm [&>ul.kenmerken-list>li]:font-medium [&>ul.kenmerken-list>li]:text-foreground [&>ul.kenmerken-list>li]:before:content-['✓'] [&>ul.kenmerken-list>li]:before:text-foreground [&>ul.kenmerken-list>li]:before:font-bold [&>ul.kenmerken-list>li]:before:text-base
                    [&>ul.ingredient-list]:list-none [&>ul.ingredient-list]:p-6 [&>ul.ingredient-list]:bg-surface [&>ul.ingredient-list]:rounded-2xl [&>ul.ingredient-list]:my-6 [&>ul.ingredient-list]:[column-count:1] [&>ul.ingredient-list]:sm:[column-count:2] [&>ul.ingredient-list]:lg:[column-count:3] [&>ul.ingredient-list]:[column-gap:2rem]
                    [&>ul.ingredient-list>li]:break-inside-avoid [&>ul.ingredient-list>li]:py-1 [&>ul.ingredient-list>li]:text-[13.5px] [&>ul.ingredient-list>li]:text-text-secondary [&>ul.ingredient-list>li]:font-mono [&>ul.ingredient-list>li]:tracking-tight
                    [&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-foreground/30 [&_a]:text-foreground [&_a:hover]:decoration-foreground
                    [&_strong]:font-semibold [&_strong]:text-foreground
                    [&_em]:italic
                    [&_blockquote]:border-l-2 [&_blockquote]:border-foreground/30 [&_blockquote]:pl-5 [&_blockquote]:italic [&_blockquote]:text-text-secondary [&_blockquote]:my-6
                    [&_table]:w-full [&_table]:my-8 [&_table]:border-collapse [&_table]:rounded-2xl [&_table]:overflow-hidden [&_table]:bg-surface/60
                    [&_tr]:border-b [&_tr]:border-border [&_tr:last-child]:border-b-0
                    [&_td]:p-5 [&_td]:align-top [&_td]:text-[14.5px] [&_td]:leading-[1.7] [&_td]:border-r [&_td]:border-border [&_td:last-child]:border-r-0
                    [&_td>p]:m-0 [&_td>p]:mb-2 [&_td>p:last-child]:mb-0
                    [&_th]:p-5 [&_th]:text-left [&_th]:text-[11px] [&_th]:uppercase [&_th]:tracking-[0.1em] [&_th]:text-text-tertiary [&_th]:font-semibold [&_th]:bg-surface [&_th]:border-b [&_th]:border-border [&_th]:border-r [&_th]:last:border-r-0
                    [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-2xl [&_img]:my-6
                    [&_iframe]:max-w-full [&_iframe]:my-6 [&_iframe]:rounded-2xl"
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              ) : (
                <p className="text-[15px] text-text-tertiary italic">
                  {t("comingSoon")}
                </p>
              )}
            </div>

            {toc.length >= 3 && (
              <aside className="hidden lg:block">
                <div className="sticky top-24">
                  <p className="label-sm mb-4">{t("onThisPage")}</p>
                  <ul className="border-l border-border space-y-2">
                    {toc.map((entry) => (
                      <li key={entry.id}>
                        <a
                          href={`#${entry.id}`}
                          className={`block -ml-px border-l border-transparent hover:border-foreground/40 pl-4 py-1 text-[13px] leading-snug text-text-secondary hover:text-foreground transition-colors ${
                            entry.level === 3 ? "pl-7 text-text-tertiary" : ""
                          }`}
                          style={{ transitionDuration: "var(--duration-fast)" }}
                        >
                          {entry.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            )}
          </div>

          {page.children.length > 0 && (
            <section className="mt-14 pt-10 border-t border-border">
              <p className="label-sm mb-4">{t("moreIn", { section: page.title })}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {page.children
                  .filter((c) => c.hasContent)
                  .map((c) => (
                    <Link
                      key={c.slug}
                      href={`/${c.slug}`}
                      className="group flex items-center justify-between p-5 border border-border hover:bg-surface transition-colors rounded-xl"
                      style={{ transitionDuration: "var(--duration-fast)" }}
                    >
                      <h3 className="text-sm font-medium text-foreground">
                        {c.title}
                      </h3>
                      <svg
                        className="w-4 h-4 text-text-tertiary group-hover:text-foreground shrink-0 transition-all group-hover:translate-x-1"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      >
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </Link>
                  ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileTabBar />
    </>
  );
}
